import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Fragment, useContext, useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { storesContext } from '../../stores/storesContext';
import SeasonalStrategyChart from '../charts/SeasonalStrategyChart';
import WeeklySoilMoistureChart from '../charts/WeeklySoilMoistureChart';
import { ERROR_TYPES, waterConsentPeriodString } from './helpers';
import IrrigationLegend from './IrrigationLegend';
import SummaryMap from './maps/SummaryMap';
import LastWeekRain from './tables/LastWeekRain';
import LastWeekTable from './tables/LastWeekTable';
import RecommendationTable from './tables/RecommendationTable';
import SeasonToDateTable from './tables/SeasonToDateTable';

const TerraprobeSite = ({
  selectedSite,
  siteSummaries,
  siteReadings,
  siteStrategies,
  waterConsent,
  enrichedSites,
  isPrinting,
  selectSite
}) => {
  const userStore = useContext(storesContext);

  const selectedProperty = userStore.propertiesStore.selectedProperty;

  // Build a version of the selected property with enriched IMS records for use
  // in the embedded SummaryMap, so map pins have correct coordinates and names.
  // Falls back to the raw property if enrichment has not yet resolved.
  const propertyForMap = enrichedSites
    ? { ...selectedProperty, irrigation_monitoring_sites: enrichedSites }
    : selectedProperty;

  /**
   * Returns the summary for a site.
   *
   * The summary has summary has the data we need to calculate the current
   * status of the site.
   *
   * @param {object} site
   * @returns {(object|null)}
   */
  const siteSummary = (site) =>
    siteSummaries.find(
      (siteSummary) => siteSummary.site_id === Number(site.site_number)
    );

  /**
   * Returns all probe readings for the selected site.
   *
   * @returns {(Array|null)}
   */
  const selectedSiteProbeReadings = useMemo(() => {
    if (selectedSite)
      return siteReadings[selectedSite.id]
        ? siteReadings[selectedSite.id].filter(
            (reading) => reading.type === 'Probe'
          )
        : null;
    else return null;
  }, [siteReadings, selectedSite]);

  /**
   * Returns the latest probe reading for the selected site.
   *
   * @returns {(object|null)}
   */
  const latestWeekReading = useMemo(() => {
    return selectedSiteProbeReadings && selectedSiteProbeReadings.length > 0
      ? selectedSiteProbeReadings[0]
      : null;
  }, [selectedSiteProbeReadings]);

  const waterConsentData = useMemo(() => {
    if (waterConsent.error) {
      const type = waterConsent.error.type;
      switch (type) {
        case ERROR_TYPES.NoWaterConsentError:
          return {
            error: {
              level: 'info',
              message: (
                <div>
                  No consent information. Contact{' '}
                  <a href='mailto:AngusBews@fruition.net.nz'>
                    AngusBews@fruition.net.nz
                  </a>{' '}
                  to enable.
                </div>
              )
            }
          };
        case ERROR_TYPES.NoWaterConsentDataError:
          return {
            error: {
              level: 'warning',
              message: (
                <div>No consent data was found for the selected period.</div>
              )
            }
          };
        case ERROR_TYPES.WaterConsentDataFetchError:
          return {
            error: {
              level: 'warning',
              message: (
                <div>
                  Error retrieving consent information, contact{' '}
                  <a href='mailto:AngusBews@fruition.net.nz'>
                    AngusBews@fruition.net.nz
                  </a>{' '}
                  for support.
                </div>
              )
            }
          };
        default:
          return {
            error: {
              level: 'warning',
              message: (
                <div>
                  An unexpected error occured. Contact{' '}
                  <a href='mailto:AngusBews@fruition.net.nz'>
                    AngusBews@fruition.net.nz
                  </a>{' '}
                  for support.
                </div>
              )
            }
          };
      }
    }
    const waterConsentData = waterConsent.data;
    const waterConsentValues = waterConsentData.values;
    const waterConsentLastDate = waterConsentData.lastDate;
    const propertyHilltopConsent = selectedProperty?.property_hilltop_consent;
    const totalWaterConsent = propertyHilltopConsent?.consent_limit;
    const period = propertyHilltopConsent?.period_days;
    const amountWaterUsed = Math.round(
      waterConsentValues.reduce((a, b) => a + b, 0)
    );
    const amountWaterRemaining = totalWaterConsent - amountWaterUsed;
    const percentWaterUsed = (amountWaterUsed / totalWaterConsent) * 100;
    const lastDate = moment(waterConsentLastDate);
    return {
      totalWaterConsent,
      amountWaterUsed: amountWaterUsed,
      amountWaterRemaining: amountWaterRemaining,
      percentWaterUsed: percentWaterUsed.toFixed(1),
      lastDate: lastDate.format('DD/MM/YYYY LT'),
      lastDateDays: moment().diff(lastDate, 'days'),
      period
    };
  }, [
    selectedProperty?.property_hilltop_consent,
    waterConsent.data,
    waterConsent.error
  ]);

  /**
   * Map water consent error level to Bootstrap alert variant.
   */
  const waterConsentErrorLevelAlertVariant = (level) => {
    switch (level) {
      case 'info':
        return 'info';
      case 'warning':
        return 'warning';
      default:
        return 'primary';
    }
  };

  return (
    <>
      {Object.keys(siteReadings).some(
        (s) => Number(s) === Number(selectedSite.id)
      ) ? (
        <Fragment>
          <Row className='my-2'>
            <Col
              xl={6}
              lg={6}
              md={12}
              xs={isPrinting ? 6 : 12}
              className='my-2'
            >
              <Card>
                <Card.Header>
                  <b>Current State</b>
                </Card.Header>
                <Card.Body>
                  {selectedSiteProbeReadings &&
                  selectedSiteProbeReadings.length > 0 ? (
                    <Row>
                      <Col>
                        <WeeklySoilMoistureChart
                          readingData={siteReadings[selectedSite.id]}
                        />
                        <small className='d-block text-center'>
                          Root Zone: 0 - {latestWeekReading.rz1_bottom} cm
                        </small>
                      </Col>
                    </Row>
                  ) : (
                    'N/A'
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col
              xl={6}
              lg={6}
              md={12}
              xs={isPrinting ? 6 : 12}
              className='my-2'
            >
              <Card className='mb-2'>
                <Card.Header>
                  <b>Recommendation</b>
                </Card.Header>
                <Card.Body>
                  {latestWeekReading ? latestWeekReading.comment : null}
                </Card.Body>
              </Card>

              <Card className='mb-2'>
                <Card.Header>
                  <b>Schedule</b>
                </Card.Header>
                <Card.Body>
                  <RecommendationTable
                    readingData={siteReadings[selectedSite.id]}
                    site={selectedSite}
                  />
                  <Row>
                    <Col className='text-center'>
                      <Button
                        variant='outline-info'
                        as={Link}
                        to='/programming-guide'
                      >
                        Property Programming Guide
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <b>
                    Property Water Consent{' '}
                    {!waterConsentData.error &&
                      `(${waterConsentData.percentWaterUsed}%
                    used) - ${waterConsentPeriodString(
                      waterConsentData.period,
                      false
                    )} period`}
                  </b>
                </Card.Header>
                <Card.Body>
                  {waterConsentData.error ? (
                    <Row>
                      <Col>
                        <Alert
                          className='mb-0'
                          variant={waterConsentErrorLevelAlertVariant(
                            waterConsentData.error.level
                          )}
                        >
                          {waterConsentData.error.message}
                        </Alert>
                      </Col>
                    </Row>
                  ) : (
                    <React.Fragment>
                      <Row>
                        <Col className='pr-0' xs={6}>
                          Total Consent:
                        </Col>
                        <Col className='p-0 text-right' xs={3}>
                          {waterConsentData.totalWaterConsent.toLocaleString(
                            'en-US'
                          )}{' '}
                          m<sup>3</sup>
                        </Col>
                      </Row>
                      <Row>
                        <Col className='pr-0' xs={6}>
                          <div className='d-flex align-items-center'>
                            <div className='mr-2'>Amount Used:</div>
                            <div
                              style={{ fontSize: '11px' }}
                            >{`(today - ${waterConsentPeriodString(
                              waterConsentData.period,
                              true
                            )})`}</div>
                          </div>
                        </Col>
                        <Col className='p-0 text-right' xs={3}>
                          {waterConsentData.amountWaterUsed.toLocaleString(
                            'en-US'
                          )}{' '}
                          m<sup>3</sup>
                        </Col>
                        {waterConsentData.period > 1 ? (
                          <Col className='d-flex align-items-center'>
                            <OverlayTrigger
                              overlay={
                                <Tooltip id='amount-used-warning'>
                                  {`No data for last ${waterConsentData.lastDateDays} days`}
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon
                                icon={faExclamationCircle}
                                color='red'
                                size='lg'
                              />
                            </OverlayTrigger>
                          </Col>
                        ) : null}
                      </Row>
                      <Row>
                        <Col className='pr-0' xs={6}>
                          <b>Amount Remaining:</b>
                        </Col>
                        <Col className='p-0 text-right' xs={3}>
                          <b>
                            {waterConsentData.amountWaterRemaining.toLocaleString(
                              'en-US'
                            )}{' '}
                            m<sup>3</sup>
                          </b>
                        </Col>
                      </Row>
                      <Row className='mt-3'>
                        <Col className='text-muted'>
                          Last updated:{' '}
                          {`${waterConsentData.lastDate} (${waterConsentData.lastDateDays} days ago)`}
                        </Col>
                      </Row>
                    </React.Fragment>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col className='my-2'>
              <Card>
                <Card.Header>
                  <b>Summary Since Last Reading</b>
                </Card.Header>
                <Card.Body>
                  Last Reading:{' '}
                  <b>
                    {latestWeekReading
                      ? moment(latestWeekReading['date'], 'DD-MM-YYYY').format(
                          'dddd, MMMM D, YYYY'
                        )
                      : 'N/A'}
                  </b>
                  <br />
                  Crop use 7 days (Forecast):{' '}
                  <b>
                    {latestWeekReading && latestWeekReading.weekly_edwu !== null
                      ? `${latestWeekReading.weekly_edwu}mm`
                      : 'N/A'}
                  </b>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className='my-2 page-break'>
            <Col>
              <Card>
                <Card.Header>
                  <b>Seasonal Summary &amp; Strategy</b>
                </Card.Header>
                <Card.Body>
                  {selectedSiteProbeReadings &&
                  selectedSiteProbeReadings.length > 0 ? (
                    <SeasonalStrategyChart
                      readingData={siteReadings[selectedSite.id]}
                      strategyData={siteStrategies[selectedSite.id]}
                      siteSummary={siteSummary(selectedSite)}
                    />
                  ) : (
                    'N/A'
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col className='my-2'>
              <Card>
                <Card.Header>
                  <b>Last Week</b>
                </Card.Header>
                <Card.Body>
                  {selectedSiteProbeReadings &&
                  selectedSiteProbeReadings.length > 0 ? (
                    <LastWeekTable
                      readingData={siteReadings[selectedSite.id]}
                      site={selectedSite}
                    />
                  ) : (
                    'N/A'
                  )}
                  <LastWeekRain readingData={siteReadings[selectedSite.id]} />
                </Card.Body>
              </Card>
            </Col>
            <Col className='my-2'>
              <Card>
                <Card.Header>
                  <b>Season To Date</b>
                </Card.Header>
                <Card.Body>
                  {selectedSiteProbeReadings &&
                  selectedSiteProbeReadings.length > 0 ? (
                    <SeasonToDateTable
                      readingData={siteReadings[selectedSite.id]}
                      className='mb-0'
                    />
                  ) : (
                    'N/A'
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          

          <Row className='my-2'>
            <Col className='my-2'>
              <Card>
                <Card.Header>
                  <b>Map</b>
                </Card.Header>
                <Card.Body>
                  <IrrigationLegend className='mb-4 text-center' />
                  <Row style={{ height: '500px', width: '100%' }}>
                    <Col>
                      <SummaryMap
                        propertySummaries={[
                          {
                            loading: false,
                            property: propertyForMap,
                            results: {
                              sites: siteSummaries
                            }
                          }
                        ]}
                        selectSite={selectSite}
                        selectedSite={selectedSite}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Fragment>
      ) : null}
    </>
  );
};

TerraprobeSite.propTypes = {
  selectedSite: PropTypes.object,
  siteSummaries: PropTypes.array,
  siteReadings: PropTypes.object,
  siteStrategies: PropTypes.object,
  enrichedSites: PropTypes.array,
  isPrinting: PropTypes.bool,
  selectSite: PropTypes.func
};

export default observer(TerraprobeSite);

